import { createTheme } from '@nextui-org/react';

interface Colors {
  primary: string;
  primaryLight: string;
  secondary: string;
  black: string;
  blackLowOpacity: string;
  c500: string;
  c400: string;
  c300: string;
  c250: string;
  c200: string;
  c100: string;
  c150: string;
  c050: string;
  neon: string;
  scroll: string;
  background: string;
  caret: string;
  white: string;
  selection: string;
  lightBlackBackground: string;
  secondaryGraph: string;
  disabledPrimaryBackground: string;
  red: string;
  darkRed: string;
  warningYellow: string;
}

interface Gradients {
  primary: string;
  overflowGradient: string;
  shimmer: string;
}

interface FontSize {
  xxs: string;
  xs: string;
  s: string;
  m: string;
  ml: string;
  mlg: string;
  l: string;
  lxl: string;
  xl: string;
  '2xl': string;
  '3xl': string;
}

interface FontWeight {
  regular: number;
  medium: number;
  semibold: number;
  bold: number;
}

export interface Breakpoints {
  smallMobile: string;
  mobile: string;
  xlMobile: string;
  tablet: string;
  desktop: string;
  large: string;
  'extra-large': string;
}

export interface Shadow {
  xs: string;
  s: string;
  m: string;
  l: string;
  xl: string;
  '2xl': string;
}

export interface Fonts {
  primary: string;
}

export interface ThemeType {
  name: string;
  colors: Colors;
  gradients: Gradients;
  fontSize: FontSize;
  lineHeight: FontSize;
  fontWeight: FontWeight;
  breakpoints: Breakpoints;
  fonts: Fonts;
  borderRadius: string;
}

export type ThemeProp = { theme: ThemeType };

export const lightTheme: ThemeType = {
  name: 'Light',
  colors: {
    primary: '#2D7FF9',
    primaryLight: '#796DF6',
    secondary: '#2D7FF9',
    neon: '#11BA5D',
    black: '#2D2D2D',
    blackLowOpacity: '#2d2d2d0d',
    c500: '#575757',
    c400: '#818181',
    c300: '#ABABAB',
    c250: '#EBEBF0',
    c200: '#D5D5D5',
    c150: '#F3F5F8',
    c100: '#EAEAEA',
    c050: '#F4F4F4',
    caret: '#444',
    scroll: '#999',
    background: '#F9F9F9',
    white: '#FFFFFF',
    selection: '#F7F2FB',
    lightBlackBackground: '#223445',
    secondaryGraph: '#B668FF',
    disabledPrimaryBackground: '#E1D3EE',
    red: '#ED0A34',
    darkRed: '#EB4747',
    warningYellow: '#FABF1C',
  },
  gradients: {
    primary: 'linear-gradient(90deg, #5EB4F9 -0.29%, #796DF6 100%)',
    overflowGradient: 'linear-gradient(270deg, #ffffff 0%, #ffffff00 100%)',
    shimmer: 'linear-gradient(to right, #f6f6f6 8%, #f0f0f0 18%, #f6f6f6 33%);',
  },
  fonts: {
    primary: 'IBM Plex Sans',
  },
  fontSize: {
    xxs: '0.625rem',
    xs: '0.75rem',
    s: '0.875rem',
    m: '0.9375rem',
    ml: '1rem',
    mlg: '1.125rem',
    l: '1.25rem',
    lxl: '1.375rem',
    xl: '1.5rem',
    '2xl': '1.875rem',
    '3xl': '2.5rem',
  },
  lineHeight: {
    xxs: '0.875rem',
    xs: '1.25rem',
    s: '1.375rem',
    m: '1.5rem',
    ml: '1.25rem',
    mlg: '1.5rem',
    l: '1.75rem',
    lxl: '2rem',
    xl: '2rem',
    '2xl': '2.5rem',
    '3xl': '3.75rem',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  breakpoints: {
    smallMobile: '375px',
    mobile: '480px',
    xlMobile: '598px',
    tablet: '768px',
    desktop: '1054px',
    large: '1200px',
    'extra-large': '1400px',
  },
  borderRadius: '6px',
};
export interface PropsTheme {
  theme: ThemeType;
}

export const darkTheme: ThemeType = {
  ...lightTheme,
  name: 'Dark',
  gradients: {
    ...lightTheme.gradients,
    shimmer: 'linear-gradient(to right, #4a5064 8%, #3c4456 18%, #4a5064 100%)',
  },
};

const fonts = {
  sans: "IBM Plex Sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;",
};

export const nextUIThemeMap = (theme: ThemeType) => {
  return createTheme({
    type: theme.name.toLocaleLowerCase(), // it could be "light" or "dark"
    theme: {
      colors: {
        // brand colors
        primaryLight: '$green200',
        primaryLightHover: '$green300',
        primaryLightActive: '$green400',
        primaryLightContrast: '$green600',
        primary: theme.colors.primary,
        primaryBorder: theme.colors.primary,
        primaryBorderHover: theme.colors.primary,
        primarySolidHover: theme.colors.primary,
        primarySolidContrast: theme.colors.white,
        primaryShadow: theme.colors.c500,
        gradient: 'linear-gradient(112deg, $blue100 -25%, $pink500 -10%, $purple500 80%)',
        inputBorder: theme.colors.c200,
        inputBackground: theme.colors.white,
        link: theme.colors.primary,
        text: theme.colors.black,
        textSecondary: theme.colors.c400,
        grayScaleText: theme.colors.c300,
        disabledBackground: theme.colors.c050,
      },
      borderWeights: {
        normal: '1px',
      },
      lineHeights: {
        xs2: '1rem',
        xs3: '1.125rem',
        sm2: '1.25rem',
        base2: '1.5rem',
      },
      radii: {
        xxs: '6px',
      },
      space: {
        '6a': '0.8125rem',
        '10a': '1.625rem',
        25: '6.125rem',
        31: '7.875rem',
      },
      fonts,
      fontSizes: {
        checkbox: '$base',
        input: '0.9375rem',
      },
    },
  });
};
